import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Cover } from "../media";
import { useLocales } from "../../core/i18n";

const zoom =
	"transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] group-focus-visible:scale-[1.03] group-active:scale-[1.03]";

// Organisation left, category right: the label pair every Impact card uses.
const Labels = ({ entry }) => (
	<div className="flex items-baseline justify-between gap-3">
		<span className="font-mono text-pp-label uppercase tracking-pp-label text-pp-muted">
			{[entry.org, entry.year].filter(Boolean).join(" · ")}
		</span>
		{entry.category && (
			<span className="font-mono text-pp-label uppercase tracking-pp-label text-pp-accent-text text-right">
				{entry.category}
			</span>
		)}
	</div>
);

const Wrapper = ({ preview, href, className, children }) =>
	preview ? (
		<div className={className}>{children}</div>
	) : (
		<Link to={href} className={className}>
			{children}
		</Link>
	);

/**
 * An Impact page in the grid: 16:9 cover, organisation · category, title,
 * Read. Takes the normalised entry (core/content normalizeEntry); shared with
 * the admin preview, where `preview` renders it without a link.
 */
export const ImpactCard = ({ entry, preview = false }) => {
	const { t } = useLocales();
	return (
		<Wrapper preview={preview} href={entry.href} className="group flex flex-col h-full">
			<div className="border border-pp-rule overflow-hidden">
				<Cover
					type="impact"
					src={entry.image}
					alt={entry.imageAlt}
					org={entry.org}
					imgClassName={zoom}
				/>
			</div>
			<div className="pt-4 flex flex-col flex-1">
				<Labels entry={entry} />
				<h3 className="font-sligoil font-bold text-pp-ink text-pp-title-sm mt-3 transition-colors duration-300 group-hover:text-pp-accent-text">
					{entry.title}
				</h3>
				<span className="mt-auto pt-4 font-mono text-pp-label uppercase tracking-pp-label text-pp-accent-text">
					→ {t("ui.cardRead")}
				</span>
			</div>
		</Wrapper>
	);
};

/**
 * A case study (pinned Impact page) as a numbered editorial row: the same
 * labels as ImpactCard plus the description, cover beside. `index` is its
 * zero-based position; `preview` renders it without a link.
 */
export const ImpactLine = ({ entry, index = 0, preview = false }) => {
	const { t } = useLocales();
	return (
		<Wrapper
			preview={preview}
			href={entry.href}
			className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 sm:py-12 border-t border-pp-rule"
		>
			<div className="md:col-span-6 lg:col-span-7 flex flex-col order-2 md:order-1">
				<div className="flex items-baseline gap-4">
					<span className="font-mono text-pp-label tracking-pp-label tabular-nums text-pp-accent-text">
						{String(index + 1).padStart(2, "0")}
					</span>
					<div className="flex-1">
						<Labels entry={entry} />
					</div>
				</div>
				<h3 className="font-sligoil font-bold text-pp-ink text-pp-title-lg mt-5 max-w-[22ch] transition-colors duration-300 group-hover:text-pp-accent-text">
					{entry.title}
				</h3>
				{entry.description && (
					<p className="text-pp-muted text-base leading-relaxed mt-4 max-w-[56ch]">
						{entry.description}
					</p>
				)}
				<span className="mt-6 md:mt-auto pt-2 font-mono text-pp-label uppercase tracking-pp-label text-pp-accent-text">
					→ {t("ui.cardRead")}
				</span>
			</div>
			<div className="md:col-span-6 lg:col-span-5 order-1 md:order-2">
				<div className="border border-pp-rule overflow-hidden">
					<Cover
						type="impact"
						src={entry.image}
						alt={entry.imageAlt}
						org={entry.org}
						imgClassName={zoom}
					/>
				</div>
			</div>
		</Wrapper>
	);
};

const entryShape = PropTypes.shape({
	href: PropTypes.string,
	title: PropTypes.string,
	org: PropTypes.string,
	year: PropTypes.string,
	category: PropTypes.string,
	description: PropTypes.string,
	image: PropTypes.string,
	imageAlt: PropTypes.string,
});
Labels.propTypes = { entry: entryShape.isRequired };
Wrapper.propTypes = {
	preview: PropTypes.bool,
	href: PropTypes.string,
	className: PropTypes.string,
	children: PropTypes.node,
};
ImpactCard.propTypes = { entry: entryShape.isRequired, preview: PropTypes.bool };
ImpactLine.propTypes = {
	entry: entryShape.isRequired,
	index: PropTypes.number,
	preview: PropTypes.bool,
};
